// src/components/SimpleFormComponents/SimpleFormItem.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Form, Tooltip } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from './SimpleFormProvider';
import { useState, useEffect, useRef, memo, useCallback } from 'react';
import dayjs from 'dayjs';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleFormItem = ({
  noLabel,
  // 'x-form-item-tooltip': xFormItemTooltip,
  'x-show-success': xShowSuccess = false,
  customValidationStatus = null,
  customValidationMessage = '',
  label,
  required,
  rules = [],
  toolTipLabel,
  labelLink, // URL to open when link icon is clicked
  labelLinkTooltip, // Tooltip for the link icon (e.g., "Sefaz SP")
  children,
  elementProps,
  style,
  validateTrigger = 'onBlur',
  showFeedback = false,

  // strip props that should not hit the DOM
  loading,
  isDatePicker = false,
  isPhone = false,
  showLabel,
  showApiInfo,

  tooltip,
  // forward only safe props
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Only destructure what we need to minimize re-renders
  const { form, setFieldValidationStatus, requiredFields, requiredFieldsAtLeads } =
    useSimpleFormContext();

  const { singleFieldName, flattenFieldName, fullFieldName } = elementProps || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Removed Form.useWatch - it caused ALL SimpleFormItems to re-render
  // on ANY form field change. Now we rely on customValidationStatus from useCustomValidationStatus
  // which only updates when THIS field's value changes.
  const prevStatusRef = useRef();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFill = validateRequiredField();

  // PERFORMANCE FIX: Use customValidationStatus directly instead of watching form value
  // customValidationStatus already handles required validation in useCustomValidationStatus hook
  const isErrored = customValidationStatus === 'error';
  const isValid = customValidationStatus === 'success';

  // Auto-generate label from field name when label prop is not provided
  // Priority: 1) singleFieldName (from useFormElement), 2) extract last part from flattenFieldName
  // This ensures labels like 'zip' instead of 'addresses_mainzip' for virtual fields
  const autoLabel = singleFieldName || extractLastFieldName(flattenFieldName);
  const translatedLabel = label ? translateX(label) : translateX(autoLabel);

  // Build label with optional tooltip and link icon
  const buildWrappedLabel = () => {
    let labelContent = translatedLabel;

    // Wrap with tooltip if provided
    if (toolTipLabel) {
      labelContent = (
        <Tooltip title={toolTipLabel}>
          <span>{labelContent}</span>
        </Tooltip>
      );
    }

    // Add link icon if labelLink is provided
    if (labelLink) {
      const handleLinkClick = e => {
        e.preventDefault();
        e.stopPropagation();
        window.open(labelLink, '_blank', 'noopener,noreferrer');
      };

      const linkIcon = (
        <Tooltip title={labelLinkTooltip ? translateX(labelLinkTooltip) : translateX('open_link')}>
          <LinkOutlined
            onClick={handleLinkClick}
            style={{
              marginLeft: 6,
              cursor: 'pointer',
              color: '#1890ff',
              fontSize: 12,
            }}
          />
        </Tooltip>
      );

      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {labelContent}
          {linkIcon}
        </span>
      );
    }

    return labelContent;
  };

  const wrappedLabel = buildWrappedLabel();

  const fullRules = [
    ...(rules || []),
    ...(requiredFill ? [{ required: true, message: translateX('required') }] : []),
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [validateStatus, setValidateStatus] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Simplified effect - only depends on customValidationStatus
  // which is already computed by useCustomValidationStatus hook
  useEffect(() => {
    let newStatus = '';

    // Use customValidationStatus directly - it already handles all validation logic
    // including required fields, min/max length, and custom validators
    if (customValidationStatus === 'error') {
      newStatus = 'error';
    } else if (customValidationStatus === 'success') {
      newStatus = xShowSuccess ? 'success' : '';
    } else if (customValidationStatus === 'validating') {
      newStatus = 'validating';
    }
    // Empty status = no feedback shown

    setValidateStatus(newStatus);

    // Update fieldsValidationStatus only if changed
    if (prevStatusRef.current !== newStatus) {
      prevStatusRef.current = newStatus;
      setFieldValidationStatus?.(fullFieldName, newStatus);
    }
  }, [customValidationStatus, xShowSuccess, fullFieldName, setFieldValidationStatus]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  function validateRequiredField() {
    let isRequired = required || requiredFields?.includes(flattenFieldName);
    if (isRequired) return true;

    // Check if 'uniqueKey' is in requiredFields and this field is a document identifier
    // uniqueKey represents: cpf.value, cnpj.value, or foreignIdentifier
    if (requiredFields?.includes('uniqueKey')) {
      const uniqueKeyFields = ['cpf.value', 'cnpj.value', 'foreignIdentifier'];
      if (uniqueKeyFields.includes(flattenFieldName)) {
        return true;
      }
    }

    if (!requiredFieldsAtLeads.includes(flattenFieldName)) return false;

    isRequired = true;
    requiredFieldsAtLeads
      .filter(f => f !== flattenFieldName)
      .map(f => {
        const fieldArr = f.split('.');
        const value = form.getFieldValue(fieldArr);
        // workaround for SimplePhone mask
        if (value && value !== '(') isRequired = false;
      });

    return isRequired;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Simplified help text logic using customValidationStatus
  const helpText = validateStatus === 'error' ? customValidationMessage || undefined : undefined;

  // Show feedback icon only when there's an error or success with xShowSuccess
  const hasFeedbackEffective =
    !!showFeedback && (validateStatus === 'error' || validateStatus === 'success');

  // ╔═══════════════════════════════════════════════════════════════════════════╗
  // ║ DATEPICKER PROTECTION: Bullet-proof normalization                        ║
  // ║ Garante que DatePicker SEMPRE receba dayjs, NUNCA strings                ║
  // ║ Suporta: DatePicker (valor único) e RangePicker (array de 2 datas)       ║
  // ╚═══════════════════════════════════════════════════════════════════════════╝

  // Normaliza um único valor de data
  // IMPORTANT: For date-only fields (without time), we need to extract just the date part
  // to avoid timezone conversion issues (e.g., "2000-03-15T00:00:00.000Z" becoming "2000-03-14" in UTC-3)
  const normalizeSingleDate = value => {
    if (!value) return null;
    if (dayjs.isDayjs(value)) return value;
    if (typeof value === 'string') {
      // Extract date-only part (YYYY-MM-DD) from ISO string to avoid timezone shift
      // "2000-03-15T00:00:00.000+00:00" → "2000-03-15" → dayjs parses as local date
      const dateOnly = value.includes('T') ? value.split('T')[0] : value;
      const converted = dayjs(dateOnly);
      return converted.isValid() ? converted : null;
    }
    if (value instanceof Date) {
      // For Date objects, extract date parts to avoid timezone issues
      const dateOnly = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
      const converted = dayjs(dateOnly);
      return converted.isValid() ? converted : null;
    }
    return null;
  };

  // Normaliza valor: pode ser data única OU array de datas (RangePicker)
  const normalizeDateValue = value => {
    if (!value) return null;

    // Se é array (RangePicker): normaliza cada elemento
    if (Array.isArray(value)) {
      return value.map(item => normalizeSingleDate(item));
    }

    // Se é valor único (DatePicker): normaliza direto
    return normalizeSingleDate(value);
  };

  const getValueFromEventDate = value => {
    if (!value) return null;

    // Se é array (RangePicker): normaliza cada elemento
    if (Array.isArray(value)) {
      return value.map(item => normalizeSingleDate(item));
    }

    // Se é valor único (DatePicker): normaliza direto
    return normalizeSingleDate(value);
  };

  // getValueProps: Intercepta o valor ANTES de passar para o children (DatePicker)
  // Isso garante que mesmo que o form tenha strings, o DatePicker recebe dayjs
  const getValuePropsDate = value => {
    return { value: normalizeDateValue(value) };
  };

  // Props extras quando isDatePicker=true
  const datePickerProps = isDatePicker
    ? {
        normalize: normalizeDateValue,
        getValueFromEvent: getValueFromEventDate,
        getValueProps: getValuePropsDate,
      }
    : {};

  // ╔═══════════════════════════════════════════════════════════════════════════╗
  // ║ PHONE PROTECTION: Extract phoneNumber from metaObj                       ║
  // ║ PhoneInput calls onChange(ev, profile, metaObj)                          ║
  // ║ We need to extract metaObj.phoneNumber for form storage                 ║
  // ╚═══════════════════════════════════════════════════════════════════════════╝

  // PhoneInput onChange creates a synthetic event with phoneNumber in target.value
  // Ant Design's Form.Item getValueFromEvent only receives the FIRST argument
  const getValueFromEventPhone = ev => {
    // SimplePhone wraps the phoneNumber in a synthetic event
    // Extract phoneNumber from ev.target.value
    if (ev?.target?.value) {
      return ev.target.value;
    }

    // Fallback: empty string
    return '';
  };

  const phoneProps = isPhone
    ? {
        getValueFromEvent: getValueFromEventPhone,
      }
    : {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Form.Item
      name={fullFieldName}
      label={!noLabel && wrappedLabel}
      rules={fullRules}
      hasFeedback={hasFeedbackEffective}
      validateTrigger={validateTrigger}
      validateStatus={validateStatus}
      style={{ width: '100%', marginBottom: noLabel ? 0 : 24, ...style }}
      required={requiredFill}
      // tooltip={xFormItemTooltip}
      tooltip={translateX(tooltip)}
      help={helpText}
      {...datePickerProps}
      {...phoneProps}
      {...props}
    >
      {children}
    </Form.Item>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default memo(SimpleFormItem);

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Extracts the last field name from a flattened field path
 * Used for auto-generating labels from field names
 * @param {string} flattenFieldName - Flattened field name (e.g., 'addresses_.main.zip')
 * @returns {string} Last part of the field name (e.g., 'zip')
 *
 * Examples:
 * - 'zip' → 'zip'
 * - 'addresses_.main.zip' → 'zip'
 * - 'entity.name' → 'name'
 * - 'contacts_.mobile.value' → 'value'
 */
function extractLastFieldName(flattenFieldName) {
  if (!flattenFieldName) return '';

  // Split by '.' and get the last part
  const parts = String(flattenFieldName).split('.');
  return parts[parts.length - 1] || '';
}
